import { useQuery } from '@tanstack/react-query';
import { ReactNode, useCallback, useMemo, useState } from 'react';

import { queries } from '../api/queryKeys/queries';
import {
    clearGroupContextId,
    getGroupContextCompanyIds,
    getGroupContextId,
    getGroupContextType,
    setGroupContextCompanyIds,
    setGroupContextId,
    setGroupContextType,
} from '../utils/groupContextStorage';
import {
    isCompanyContextType,
    isCustomerContextType,
    isGroupContextType,
    isSupplierContextType,
} from '../utils/groupContextTypeUtils';

import { GroupContext, GroupContextState, GroupContextValue } from './groupContextContext';
import { useRole } from './useRole';

const readGroupContext = (): GroupContextState => {
    const groupId = getGroupContextId();
    const companyIds = getGroupContextCompanyIds();
    const type = getGroupContextType();

    return {
        groupId,
        companyIds,
        type,
    };
};

export const GroupContextProvider = ({ children }: { children: ReactNode }) => {
    const [state, setState] = useState<GroupContextState>(() => readGroupContext());
    const role = useRole();
    const isSuperAdmin = role.isSuperAdmin();

    const customerGroupQuery = useQuery({
        ...queries.customerGroups.detail(state.groupId ?? ''),
        enabled: isSuperAdmin && isGroupContextType(state.type) && isCustomerContextType(state.type) && !!state.groupId,
    });

    const supplierGroupQuery = useQuery({
        ...queries.supplierGroups.detail(state.groupId ?? ''),
        enabled: isSuperAdmin && isGroupContextType(state.type) && isSupplierContextType(state.type) && !!state.groupId,
    });

    const customerQuery = useQuery({
        ...queries.customers.detail(state.companyIds[0] ?? ''),
        enabled:
            isSuperAdmin &&
            isCompanyContextType(state.type) &&
            isCustomerContextType(state.type) &&
            !!state.companyIds[0],
    });

    const supplierQuery = useQuery({
        ...queries.suppliers.detail(state.companyIds[0] ?? ''),
        enabled:
            isSuperAdmin &&
            isCompanyContextType(state.type) &&
            isSupplierContextType(state.type) &&
            !!state.companyIds[0],
    });

    const name = useMemo(() => {
        if (!isSuperAdmin || !state.type) {
            return null;
        }

        if (isCompanyContextType(state.type)) {
            if (isCustomerContextType(state.type)) {
                return customerQuery.data?.name ?? null;
            }

            if (isSupplierContextType(state.type)) {
                return supplierQuery.data?.name ?? null;
            }

            return null;
        }

        if (isCustomerContextType(state.type)) {
            return customerGroupQuery.data?.name ?? null;
        }

        if (isSupplierContextType(state.type)) {
            return supplierGroupQuery.data?.name ?? null;
        }

        return null;
    }, [
        customerGroupQuery.data?.name,
        customerQuery.data?.name,
        isSuperAdmin,
        state.type,
        supplierGroupQuery.data?.name,
        supplierQuery.data?.name,
    ]);

    const setGroupContext = useCallback((nextState: GroupContextState) => {
        if (!nextState.groupId || !nextState.type) {
            clearGroupContextId();
            setState({ groupId: null, companyIds: [], type: null });
            return;
        }

        setGroupContextType(nextState.type);
        setGroupContextCompanyIds(nextState.companyIds);
        setGroupContextId(nextState.groupId);
        setState(nextState);
    }, []);

    const clearGroupContext = useCallback(() => {
        clearGroupContextId();
        setState({ groupId: null, companyIds: [], type: null });
    }, []);

    const value = useMemo<GroupContextValue>(
        () => ({
            ...state,
            name,
            setGroupContext,
            clearGroupContext,
        }),
        [clearGroupContext, name, setGroupContext, state]
    );

    return <GroupContext.Provider value={value}>{children}</GroupContext.Provider>;
};
